import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { dashboard } from '@cwos/api-client';
import type { dashboard as DashboardNS } from '@cwos/api-client';
import { Button, Pill, SkeletonSet, SuccessEmpty } from '@cwos/ui-admin';
import { usePermissions } from '../../permissions.js';
import { authorityGate, gateOpen, type AuthorityGate } from '../../authorityGate.js';
import { GateNotice } from '../../GateNotice.js';
import { ANALYTICS_PRESETS, type AnalyticsPreset } from '../analytics/analyticsRange.js';
import { WidgetCard } from './WidgetRenderer.js';
import { cycleWidth, moveItem, toWireWidget } from './layoutModel.js';
import type { DraftWidget } from './layoutModel.js';
import { thresholdStep, vizLabel } from './metricFormat.js';
import { LoadFailure } from '../../LoadFailure.js';

type LayoutView = DashboardNS.DashboardLayoutView;
type MetricDefinition = DashboardNS.MetricDefinition;

/**
 * ConfigurableDashboard — the customer-configurable dashboard (owner directive
 * #5050 §2). Customers can add/remove/reorder/resize widgets, pick a
 * visualization, configure thresholds, select a reporting period, save layouts,
 * restore defaults, maintain multiple layouts, set an org-default, and keep a
 * personal dashboard. Only the curated metric catalog is offered — no arbitrary
 * query building.
 *
 * View is `dashboard:read`; edits require `dashboard:manage`.
 */
export function ConfigurableDashboard() {
  const permissions = usePermissions();
  /**
   * FOUR ANSWERS, NOT TWO — and the boolean has to travel, which is why the
   * GATE travels with it.
   *
   * `canManage` is a prop on four child components and gates a `disabled=`, a
   * Delete button and two panels. Default-deny is right for every one of those.
   * It is wrong for the one place the editor turns it into WORDS ("You need the
   * dashboard:manage permission to edit layouts"), because `false` there is
   * also "the permission set could not be re-read" — and this route is gated on
   * an ANALYTICS key, not on `dashboard:manage`, so a reader who is let through
   * on a cached analytics grant gets that sentence about a key nobody could
   * check.
   */
  const manageGate = authorityGate(permissions, 'dashboard:manage');
  const canManage = gateOpen(manageGate);
  const queryClient = useQueryClient();

  const metricsQ = useQuery({
    queryKey: ['dashboards', 'metrics'],
    queryFn: () => dashboard.metricCatalog(),
  });
  const templatesQ = useQuery({
    queryKey: ['dashboards', 'templates'],
    queryFn: () => dashboard.dashboardTemplates(),
  });
  const layoutsQ = useQuery({
    queryKey: ['dashboards', 'layouts'],
    queryFn: () => dashboard.dashboardLayouts(),
  });

  const [scope, setScope] = useState<'org' | 'personal'>('org');
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState<DraftWidget[]>([]);
  const [draftName, setDraftName] = useState('');
  const [draftPreset, setDraftPreset] = useState<AnalyticsPreset>('this_week');

  const layouts = layoutsQ.data?.layouts ?? [];
  const orgLayouts = useMemo(() => layouts.filter((l) => l.scope === 'org'), [layouts]);
  const personalLayout = useMemo(() => layouts.find((l) => l.scope === 'personal'), [layouts]);

  const activeLayout = useMemo<LayoutView | undefined>(() => {
    if (scope === 'personal') return personalLayout;
    return orgLayouts.find((l) => l.isDefault) ?? orgLayouts[0];
  }, [scope, orgLayouts, personalLayout]);

  if (metricsQ.isLoading || layoutsQ.isLoading) return <SkeletonSet surface="table" rows={5} />;
  if (metricsQ.isError && !metricsQ.data) {
    return (
      <LoadFailure
        error={metricsQ.error}
        message="We couldn’t load the metric catalog just now."
        onRetry={() => void metricsQ.refetch()}
      />
    );
  }
  if (layoutsQ.isError && !layoutsQ.data) {
    return (
      <LoadFailure
        error={layoutsQ.error}
        message="We couldn’t load your dashboards just now."
        onRetry={() => void layoutsQ.refetch()}
      />
    );
  }

  const metrics = metricsQ.data?.metrics ?? [];
  const families = metricsQ.data?.families ?? [];
  const templates = templatesQ.data?.templates ?? [];

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: ['dashboards', 'layouts'] });

  const startEdit = (layout: LayoutView) => {
    setScope(layout.scope);
    setEditing(true);
    setDraftName(layout.name);
    setDraftPreset(layout.periodPreset);
    setDraft(
      layout.widgets.map((w) => ({
        metricKey: w.metricKey,
        visualization: w.visualization,
        width: w.width,
        config: w.config,
      })),
    );
  };

  const startNew = (from?: DashboardNS.DashboardTemplate) => {
    setEditing(true);
    if (from) {
      setDraftName(from.name);
      setDraftPreset(from.periodPreset);
      setDraft(
        from.widgets.map((w) => ({
          metricKey: w.metricKey,
          visualization: w.visualization,
          width: w.width,
          config: {},
        })),
      );
    } else {
      setDraftName(scope === 'personal' ? 'My dashboard' : 'New dashboard');
      setDraftPreset('this_week');
      setDraft([]);
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      const wireWidgets = draft.map(toWireWidget);
      if (activeLayout) {
        await dashboard.updateDashboardLayout(activeLayout.id, {
          name: draftName,
          scope,
          periodPreset: draftPreset,
          widgets: wireWidgets,
        });
      } else {
        await dashboard.createDashboardLayout({
          name: draftName,
          scope,
          widgets: wireWidgets,
        });
      }
      await invalidate();
      setEditing(false);
    } finally {
      setBusy(false);
    }
  };

  const applyTemplate = async (tpl: DashboardNS.DashboardTemplate) => {
    setBusy(true);
    try {
      await dashboard.createDashboardLayout({ templateId: tpl.id, name: tpl.name, scope });
      await invalidate();
      setEditing(false);
    } finally {
      setBusy(false);
    }
  };

  const setDefault = async (id: string) => {
    setBusy(true);
    try {
      await dashboard.setDefaultDashboardLayout(id);
      await invalidate();
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string) => {
    setBusy(true);
    try {
      await dashboard.deleteDashboardLayout(id);
      await invalidate();
      setEditing(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="an-stack">
      <div className="an-head">
        {/* CANONICAL NAME (founder Phase A approval): ONE Dashboard and ONE
            Analytics — the plural heading read as a different concept. The
            route keeps its legacy /dashboards path underneath. */}
        <h1 className="rn-card-title">Dashboard</h1>
        <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
          The organization at a glance — every figure opens to the rows behind it.
        </p>
      </div>

      {!editing ? (
        <>
          <Toolbar
            scope={scope}
            onScope={setScope}
            canManage={canManage}
            onEdit={() => (activeLayout ? startEdit(activeLayout) : startNew())}
            onNew={() => startNew()}
          />

          {scope === 'org' && canManage ? (
            <OrgPicker
              layouts={orgLayouts}
              activeId={activeLayout?.id ?? null}
              onPick={(l) => startEdit(l)}
              onDefault={(id) => void setDefault(id)}
            />
          ) : null}

          {activeLayout ? (
            <WidgetGrid layout={activeLayout} metrics={metricsQ.data?.metrics ?? []} />
          ) : scope === 'org' && templates.length > 0 ? (
            /* THE FIRST-RUN PRESET (owner Phase G ruling): a dashboard must be
               useful before anyone configures it, so an organization with no
               saved layout gets Phibian's first template rendered LIVE — real
               widgets over real reads, not a blank builder. Saving it (a
               manage holder) turns the preview into the org layout the editor
               then customizes; nothing is stored until they do. */
            <>
              <div className="rn-card db-preset-banner">
                <div>
                  <h2 className="rn-card-title">{templates[0]!.name}</h2>
                  <p className="rn-muted" style={{ margin: 0 }}>
                    Phibian’s recommended operating view — live now, saved only when you keep it.
                  </p>
                </div>
                {canManage ? (
                  <div style={{ display: 'flex', gap: 'var(--space-8)', flexWrap: 'wrap' }}>
                    <Button small onClick={() => void applyTemplate(templates[0]!)}>
                      Keep this dashboard
                    </Button>
                    {templates.slice(1).map((t) => (
                      <button
                        key={t.id}
                        type="button"
                        className="rn-tab"
                        onClick={() => void applyTemplate(t)}
                      >
                        {t.name}
                      </button>
                    ))}
                    <button type="button" className="rn-tab" onClick={() => startNew()}>
                      Start blank
                    </button>
                  </div>
                ) : null}
              </div>
              <WidgetGrid
                layout={previewFromTemplate(templates[0]!)}
                metrics={metricsQ.data?.metrics ?? []}
              />
            </>
          ) : (
            <EmptyDashboards
              scope={scope}
              canManage={canManage}
              templates={templates}
              templatesFailed={templatesQ.isError && templatesQ.data === undefined}
              onTemplate={(t) => void applyTemplate(t)}
              onNew={() => startNew()}
            />
          )}
        </>
      ) : (
        <Editor
          metrics={metrics}
          families={families}
          templates={templates}
          draft={draft}
          name={draftName}
          preset={draftPreset}
          canManage={canManage}
          manageGate={manageGate}
          onRetryPermissions={() => permissions.refetch()}
          busy={busy}
          isNew={!activeLayout}
          onName={setDraftName}
          onPreset={setDraftPreset}
          onDraft={setDraft}
          onSave={() => void save()}
          onCancel={() => setEditing(false)}
          onRestore={(tpl) => {
            setDraftName(tpl.name);
            setDraftPreset(tpl.periodPreset);
            setDraft(
              tpl.widgets.map((w) => ({
                metricKey: w.metricKey,
                visualization: w.visualization,
                width: w.width,
                config: {},
              })),
            );
          }}
          onDelete={activeLayout ? () => void remove(activeLayout.id) : undefined}
        />
      )}
    </div>
  );
}

interface EditorProps {
  metrics: MetricDefinition[];
  families: DashboardNS.MetricFamilyInfo[];
  templates: DashboardNS.DashboardTemplate[];
  draft: DraftWidget[];
  name: string;
  preset: AnalyticsPreset;
  canManage: boolean;
  /**
   * The same question `canManage` answers, with the two states it cannot carry.
   * The editor renders a SENTENCE off it; `canManage` stays for the controls.
   */
  manageGate: AuthorityGate;
  onRetryPermissions: () => void;
  busy: boolean;
  isNew: boolean;
  onName: (v: string) => void;
  onPreset: (v: AnalyticsPreset) => void;
  onDraft: (v: DraftWidget[]) => void;
  onSave: () => void;
  onCancel: () => void;
  onRestore: (tpl: DashboardNS.DashboardTemplate) => void;
  onDelete?: () => void;
}

function Editor({
  metrics,
  families,
  templates,
  draft,
  name,
  preset,
  canManage,
  manageGate,
  onRetryPermissions,
  busy,
  isNew,
  onName,
  onPreset,
  onDraft,
  onSave,
  onCancel,
  onRestore,
  onDelete,
}: EditorProps) {
  const metricByKey = useMemo(() => new Map(metrics.map((m) => [m.key, m])), [metrics]);
  const [pickerOpen, setPickerOpen] = useState(false);

  const addWidget = (m: MetricDefinition) => {
    onDraft([
      ...draft,
      { metricKey: m.key, visualization: m.defaultVisualization, width: 'full', config: {} },
    ]);
    setPickerOpen(false);
  };

  const patch = (i: number, p: Partial<DraftWidget>) =>
    onDraft(draft.map((w, idx) => (idx === i ? { ...w, ...p } : w)));

  return (
    <div className="an-stack">
      <div className="rn-card">
        <div className="rn-card-head">
          <input
            value={name}
            onChange={(e) => onName(e.target.value)}
            className="an-date-input"
            style={{ fontSize: 15, fontWeight: 700, width: '100%' }}
            aria-label="Dashboard name"
          />
        </div>

        {/* Filter group, not a tab strip — see AnalyticsShell for the reasoning. */}
        <div
          className="rn-tabs"
          role="group"
          aria-label="Reporting period"
          style={{ marginBottom: 'var(--space-12)' }}
        >
          {ANALYTICS_PRESETS.map((p) => (
            <button
              key={p.value}
              type="button"
              className="rn-tab"
              aria-pressed={preset === p.value}
              onClick={() => onPreset(p.value)}
            >
              {p.label}
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 'var(--space-8)', flexWrap: 'wrap' }}>
          <button
            type="button"
            className="rn-tab"
            onClick={() => setPickerOpen((v) => !v)}
            aria-pressed={pickerOpen}
          >
            + Add widget
          </button>
          {templates.map((t) => (
            <button
              key={t.id}
              type="button"
              className="rn-tab"
              onClick={() => onRestore(t)}
              title={`Restore ${t.name}`}
            >
              Restore: {t.name}
            </button>
          ))}
        </div>

        {pickerOpen ? (
          <div className="rn-card" style={{ marginTop: 'var(--space-12)', boxShadow: 'none' }}>
            {families.map((f) => (
              <div key={f.key} style={{ marginBottom: 'var(--space-12)' }}>
                <h3 className="rn-card-title" style={{ fontSize: 13 }}>
                  {f.label}
                </h3>
                <div style={{ display: 'flex', gap: 'var(--space-8)', flexWrap: 'wrap' }}>
                  {metrics
                    .filter((m) => m.family === f.key)
                    .map((m) => (
                      <button
                        key={m.key}
                        type="button"
                        className="rn-tab"
                        onClick={() => addWidget(m)}
                      >
                        {m.label}
                      </button>
                    ))}
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </div>

      {draft.length === 0 ? (
        <SuccessEmpty
          heading="No widgets yet"
          message="Add a widget above to build this dashboard."
        />
      ) : (
        draft.map((w, i) => {
          const m = metricByKey.get(w.metricKey);
          return (
            <div key={`${w.metricKey}:${i}`} className="rn-card">
              <div className="rn-card-head">
                <h2 className="rn-card-title">{m?.label ?? w.metricKey}</h2>
                <span className="rn-muted">{m ? m.description : ''}</span>
              </div>

              <div
                style={{
                  display: 'flex',
                  gap: 'var(--space-8)',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                }}
              >
                {m?.visualizations.map((v) => (
                  <button
                    key={v}
                    type="button"
                    className="rn-tab"
                    aria-pressed={w.visualization === v}
                    onClick={() => patch(i, { visualization: v })}
                  >
                    {vizLabel(v)}
                  </button>
                ))}
                {w.visualization !== 'table' ? (
                  <button
                    type="button"
                    className="rn-tab"
                    onClick={() => patch(i, { width: cycleWidth(w.width) })}
                  >
                    Width: {w.width}
                  </button>
                ) : null}
                {m?.supportsThreshold && m.threshold ? (
                  <label
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 'var(--space-8)',
                      fontSize: 12.5,
                      fontWeight: 600,
                    }}
                  >
                    {m.threshold.label}
                    <input
                      type="number"
                      min={m.threshold.min}
                      max={m.threshold.max}
                      step={thresholdStep(m.threshold.unit)}
                      value={w.config.threshold ?? m.threshold.defaultValue}
                      onChange={(e) => patch(i, { config: { threshold: Number(e.target.value) } })}
                      className="an-date-input"
                      style={{ width: 88 }}
                    />
                  </label>
                ) : null}
                <span style={{ flex: 1 }} />
                <button
                  type="button"
                  className="rn-link"
                  onClick={() => onDraft(moveItem(draft, i, 'up'))}
                  aria-label="Move up"
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="rn-link"
                  onClick={() => onDraft(moveItem(draft, i, 'down'))}
                  aria-label="Move down"
                >
                  ↓
                </button>
                <button
                  type="button"
                  className="rn-link"
                  onClick={() => onDraft(draft.filter((_, idx) => idx !== i))}
                  aria-label="Remove widget"
                >
                  Remove
                </button>
              </div>
            </div>
          );
        })
      )}

      <div
        style={{ display: 'flex', gap: 'var(--space-8)', alignItems: 'center', flexWrap: 'wrap' }}
      >
        <button
          type="button"
          className="rn-tab"
          onClick={onSave}
          disabled={busy || !canManage || name.trim().length === 0}
        >
          {busy ? 'Saving…' : 'Save layout'}
        </button>
        <button type="button" className="rn-tab" onClick={onCancel}>
          Cancel
        </button>
        {!isNew && onDelete && canManage ? (
          <button type="button" className="rn-tab" onClick={onDelete} disabled={busy}>
            Delete
          </button>
        ) : null}
        <GateNotice
          gate={manageGate}
          what="edit dashboard layouts"
          onRetry={onRetryPermissions}
          refusedNode={
            <span className="rn-muted">
              You need the dashboard:manage permission to edit layouts.
            </span>
          }
        />
      </div>
    </div>
  );
}

function Toolbar({
  scope,
  onScope,
  canManage,
  onEdit,
  onNew,
}: {
  scope: 'org' | 'personal';
  onScope: (v: 'org' | 'personal') => void;
  canManage: boolean;
  onEdit: () => void;
  onNew: () => void;
}) {
  return (
    <div style={{ display: 'flex', gap: 'var(--space-8)', flexWrap: 'wrap', alignItems: 'center' }}>
      {/* Filter group, not a tab strip — see AnalyticsShell for the reasoning. */}
      <div
        className="rn-tabs"
        role="group"
        aria-label="Dashboard scope"
        style={{ marginBottom: 0 }}
      >
        <button
          type="button"
          className="rn-tab"
          aria-pressed={scope === 'org'}
          onClick={() => onScope('org')}
        >
          Organization
        </button>
        <button
          type="button"
          className="rn-tab"
          aria-pressed={scope === 'personal'}
          onClick={() => onScope('personal')}
        >
          Personal
        </button>
      </div>
      {canManage ? (
        <>
          <button type="button" className="rn-tab" onClick={onEdit}>
            Edit layout
          </button>
          <button type="button" className="rn-tab" onClick={onNew}>
            New layout
          </button>
        </>
      ) : null}
    </div>
  );
}

function OrgPicker({
  layouts,
  activeId,
  onPick,
  onDefault,
}: {
  layouts: LayoutView[];
  activeId: string | null;
  onPick: (l: LayoutView) => void;
  onDefault: (id: string) => void;
}) {
  if (layouts.length === 0) return null;
  return (
    <div style={{ display: 'flex', gap: 'var(--space-8)', flexWrap: 'wrap', alignItems: 'center' }}>
      {layouts.map((l) => (
        <span
          key={l.id}
          style={{ display: 'inline-flex', gap: 'var(--space-8)', alignItems: 'center' }}
        >
          <button
            type="button"
            className="rn-tab"
            aria-pressed={l.id === activeId}
            onClick={() => onPick(l)}
          >
            {l.name}
          </button>
          {l.isDefault ? (
            <Pill variant="sky" dot>
              default
            </Pill>
          ) : (
            <button type="button" className="rn-link" onClick={() => onDefault(l.id)}>
              set default
            </button>
          )}
        </span>
      ))}
    </div>
  );
}

/** A template rendered as a live layout — the unsaved first-run preset. */
function previewFromTemplate(t: DashboardNS.DashboardTemplate): LayoutView {
  return {
    id: 'phibian-preset-preview',
    name: t.name,
    scope: 'org',
    periodPreset: t.periodPreset,
    isDefault: false,
    filters: {},
    widgets: t.widgets.map((w, i) => ({
      metricKey: w.metricKey,
      visualization: w.visualization,
      position: i,
      width: w.width,
      config: {},
    })),
  };
}

function WidgetGrid({ layout, metrics }: { layout: LayoutView; metrics: MetricDefinition[] }) {
  const periodLabel =
    ANALYTICS_PRESETS.find((p) => p.value === layout.periodPreset)?.label ?? layout.periodPreset;
  // The catalog's human name for each widget. The card header used to print
  // the raw metric KEY (`hours_actual`) over a body that already carried the
  // label — the catalog is in hand either way, so the key is only the
  // fallback for a metric the catalog no longer lists.
  const labelFor = (key: string) => metrics.find((m) => m.key === key)?.label ?? key;
  return (
    <>
      <p className="rn-muted">
        {layout.name} · {periodLabel}
      </p>
      <div className="an-grid">
        {layout.widgets.map((w, i) => (
          <div key={`${w.metricKey}:${i}`}>
            <WidgetCard
              metricKey={w.metricKey}
              label={labelFor(w.metricKey)}
              visualization={w.visualization}
              preset={layout.periodPreset}
              from=""
              to=""
              threshold={w.config.threshold}
              /**
               * THE WIDGET IS ASKED FOR AS PART OF THIS DASHBOARD, not on its
               * own. `layoutId` is what makes the server apply the layout's
               * saved FILTERS (intersected with the viewer's grants, never
               * substituted for them) and `position` is what identifies WHICH
               * widget, so the server can load its stored grouping. Neither is a
               * request for a breakdown: the grouping comes from the saved
               * widget, and a `groupBy` on the query string is refused.
               *
               * `w.position`, not `i`. They agree today because the layout view
               * sorts by position and positions are contiguous — but `i` is this
               * loop's counter and `position` is the widget's identity, and the
               * day a filtered or partial render makes them differ, `i` would
               * silently address a neighbouring widget's grouping.
               */
              layoutId={layout.id}
              widgetPosition={w.position}
            />
          </div>
        ))}
      </div>
    </>
  );
}

/**
 * `templatesQ` had no loading or error branch, so a failed template fetch made
 * this screen offer "Start blank" alone — an organisation with a dozen ready
 * templates looked like one with none, on the screen where a first-time
 * administrator decides what their dashboard is going to be.
 */
function EmptyDashboards({
  scope,
  canManage,
  templates,
  templatesFailed,
  onTemplate,
  onNew,
}: {
  scope: 'org' | 'personal';
  canManage: boolean;
  templates: DashboardNS.DashboardTemplate[];
  templatesFailed: boolean;
  onTemplate: (t: DashboardNS.DashboardTemplate) => void;
  onNew: () => void;
}) {
  return (
    <div className="rn-card">
      <h2 className="rn-card-title">
        No {scope === 'org' ? 'organization' : 'personal'} dashboard yet
      </h2>
      <p className="rn-muted" style={{ margin: 'var(--space-8) 0 var(--space-16)' }}>
        Start from a template, or build one from scratch.
      </p>
      {canManage ? (
        <>
          {templatesFailed ? (
            <p className="rn-muted" role="status" style={{ marginBottom: 'var(--space-8)' }}>
              We couldn’t load the starting templates, so only "Start blank" is offered here.
              Reloading may bring them back.
            </p>
          ) : null}
          <div style={{ display: 'flex', gap: 'var(--space-8)', flexWrap: 'wrap' }}>
            {templates.map((t) => (
              <button key={t.id} type="button" className="rn-tab" onClick={() => onTemplate(t)}>
                {t.name}
              </button>
            ))}
            <button type="button" className="rn-tab" onClick={onNew}>
              Start blank
            </button>
          </div>
        </>
      ) : (
        <p className="rn-muted">An administrator can create the first dashboard.</p>
      )}
    </div>
  );
}
